export type MappingLocalRobotPose = {
  x: number
  y: number
  angleDeg: number
  mapX?: number
  mapY?: number
  angleMapDeg?: number
}

export type MappingLocalOccupancyGrid = {
  info?: {
    resolution?: number
    width?: number
    height?: number
    origin?: { position?: { x?: number; y?: number } }
  }
  data?: number[]
}

export type MappingLocalSnapshot = {
  occupancy?: MappingLocalOccupancyGrid
  robotPose?: MappingLocalRobotPose | null
}

export type OccupancyImage = {
  src: string
  width: number
  height: number
  resolution: number
  originX: number
  originY: number
}

/** Compact mapping snapshot: PNG + pose only. Raw occupancy.data is dropped. */
export type CompactMappingLocalState = {
  occupancyImage: OccupancyImage | null
  robotPose: MappingLocalRobotPose | null
}

function clampCell(cell: number): number {
  if (cell < -1) return -1
  if (cell > 100) return 100
  return cell
}

export function occupancyToDataUrl(width: number, height: number, data: number[]): string | null {
  if (!width || !height || width * height !== data.length) return null
  if (typeof document === 'undefined') return null
  try {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    const imageData = ctx.createImageData(width, height)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const rosIndex = y * width + x
        const canvasY = height - 1 - y
        const canvasIndex = (canvasY * width + x) * 4
        const value = clampCell(Number(data[rosIndex] ?? -1))
        let shade = 140
        if (value >= 0) {
          shade = Math.round(255 - (value / 100) * 255)
        }
        imageData.data[canvasIndex] = shade
        imageData.data[canvasIndex + 1] = shade
        imageData.data[canvasIndex + 2] = shade
        imageData.data[canvasIndex + 3] = 255
      }
    }
    ctx.putImageData(imageData, 0, 0)
    return canvas.toDataURL('image/png')
  } catch {
    return null
  }
}

export function occupancyImageFromGrid(occupancy: MappingLocalOccupancyGrid | undefined): OccupancyImage | null {
  const info = occupancy?.info
  const width = Number(info?.width ?? 0)
  const height = Number(info?.height ?? 0)
  const data = occupancy?.data || []
  if (!width || !height || data.length !== width * height) return null
  const src = occupancyToDataUrl(width, height, data)
  if (!src) return null
  return {
    src,
    width,
    height,
    resolution: Number(info?.resolution ?? 0),
    originX: Number(info?.origin?.position?.x ?? 0),
    originY: Number(info?.origin?.position?.y ?? 0),
  }
}

/**
 * Convert a WS mapping snapshot into UI state that does not retain the
 * occupancy grid (`data` is width×height ints and grows with the map).
 */
export function compactMappingLocalSnapshot(raw: MappingLocalSnapshot | null | undefined): CompactMappingLocalState {
  if (!raw) {
    return { occupancyImage: null, robotPose: null }
  }
  return {
    occupancyImage: occupancyImageFromGrid(raw.occupancy),
    robotPose: raw.robotPose ?? null,
  }
}
